import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faDownload,
  faUpload,
  faFileAlt,
  faFillDrip,
  faUndo,
  faRedo,
  faMicrophone
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import jsPDF from "jspdf";
import Speech from "speak-tts";
import AILightComponent from '../../components/AILightComponent';

const VoiceBillCreator = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData, { loading: fetchLoading }] = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [initialState, setInitialState] = useState(() => location.state || {});
  const [userId] = useState(() => authState.user?._id);
  const [userName] = useState(() => authState.user?.name);

  const [isNotesSpeaking, setIsNotesSpeaking] = useState(false);
  const [commentData, setCommentData] = useState([]);
  const [postNotesClicked, setPostNotesClicked] = useState(false);
  const [postedComment, setPostedComment] = useState(false);
  const [errors, setErrors] = useState('');
  const [isConversationalNotes, setIsConversationalNotes] = useState(false);

  const [inputRemarks, setInputRemarks] = useState('');
  const [sortTags, setSortTags] = useState('');

  const [isCheckedAISupport, setIsCheckedAISupport] = useState(false);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);
  const [isHandwritingVisible, setIsHandwritingVisible] = useState(false);

  const [notesType, setNotesType] = useState("Quick Notes");
  const [userCommentTitle, setUserCommentTitle] = useState('');
  const [userCommentDetails, setUserCommentDetails] = useState('');
  const [isAudioInput, setIsAudioInput] = useState(false);

  const [downloadPDFClicked, setDownloadPDFClicked] = useState(false);
  const [viewComment, setViewComment] = useState(false);
  const [filterBy, setFilterBy] = useState("All Notes");

  // Speech
  const speech = new Speech();

  const languageOptions = [
    { code: "en-US", name: "English (United States)" },
    { code: "en-GB", name: "English (United Kingdom)" },
    { code: "ar-SA", name: "Arabic (Saudi Arabia)" },
    { code: "hi-IN", name: "Hindi (India)" },
    { code: "bn-BD", name: "Bengali (Bangladesh)" },
    { code: "ta-IN", name: "Tamil (India)" },
    { code: "te-IN", name: "Telugu (India)" },
    { code: "ur-PK", name: "Urdu (Pakistan)" },
  ];

  const [selectedLanguage, setSelectedLanguage] = useState("en-US");

  // Handlers
  const handleToggleHandwriting = () => {
    setIsHandwritingVisible((prev) => !prev);
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  const handleShowMicrophoneAlertCheckboxChange = (event) => {
    setShowMicrophoneAlert(event.target.checked);
  };

  const handleConversationalNotesCheckboxChange = (event) => {
    setIsConversationalNotes(event.target.checked);
  };

  const isAudioInputChange = (event) => {
    setIsAudioInput(event.target.checked);
  };

  // Toggle Speech for Notes
  const toggleSpeechNotes = (mynotestext) => {
    if (isNotesSpeaking) {
      speech.cancel();
      setIsNotesSpeaking(false);
    } else {
      setIsNotesSpeaking(true);
      speech.setRate(1.0);
      speech.setPitch(2.0);
      speech.setVolume(1.0);
      speech.setLanguage(selectedLanguage);
      speech.speak({ text: mynotestext });
    }
  };

  // Post Notes Handler
  const postNotes = () => {
    setNotesType('');
    setUserCommentTitle('');
    setUserCommentDetails('');
    setIsAudioInput(false);
    setErrors('');
    setPostNotesClicked(true);
    setViewComment(false);
    setPostedComment(false);
  };

  const filteredComments = useMemo(() => {
    return filterBy === "All Notes"
      ? [...commentData].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
      : [...commentData]
          .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
          .filter((comment) => comment.notesType === filterBy);
  }, [commentData, filterBy]);

  // ==================== HandwrittenNote Inner Component ====================
  const HandwrittenNote = ({ onClose }) => {
    const authStateInner = useSelector((state) => state.authReducer);
    const [userCode] = useState(authStateInner.user?.think5whyscode || '');

    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [convertedText, setConvertedText] = useState('');

    const [penSize, setPenSize] = useState(3);
    const [penColor, setPenColor] = useState('black');
    const [shapeMode, setShapeMode] = useState('free');
    const [actionsHistory, setActionsHistory] = useState([]);
    const [currentActionIndex, setCurrentActionIndex] = useState(-1);

    // Initialize canvas
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penSize;
    }, []);

    const saveCanvasState = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);

      setActionsHistory((prev) => {
        const updated = prev.slice(0, currentActionIndex + 1);
        updated.push(currentState);
        return updated;
      });
      setCurrentActionIndex((prev) => prev + 1);
    };

    const handleUndo = () => {
      if (currentActionIndex <= 0) return;
      const newIndex = currentActionIndex - 1;
      setCurrentActionIndex(newIndex);
      const ctx = canvasRef.current.getContext('2d');
      ctx.putImageData(actionsHistory[newIndex], 0, 0);
    };

    const handleRedo = () => {
      if (currentActionIndex >= actionsHistory.length - 1) return;
      const newIndex = currentActionIndex + 1;
      setCurrentActionIndex(newIndex);
      const ctx = canvasRef.current.getContext('2d');
      ctx.putImageData(actionsHistory[newIndex], 0, 0);
    };

    const handleFillCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = penColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      saveCanvasState();
    };

    // Drawing logic
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      let isCurrentlyDrawing = false;

      const getPos = (e) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: (e.clientX || e.touches[0].clientX) - rect.left,
          y: (e.clientY || e.touches[0].clientY) - rect.top,
        };
      };

      const startDrawing = (e) => {
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        isCurrentlyDrawing = true;
        setIsDrawing(true);
      };

      const draw = (e) => {
        if (!isCurrentlyDrawing) return;
        const { x, y } = getPos(e);
        ctx.lineTo(x, y);
        ctx.strokeStyle = penColor;
        ctx.lineWidth = penSize;
        ctx.lineCap = 'round';
        ctx.stroke();
      };

      const endDrawing = () => {
        if (isCurrentlyDrawing) {
          ctx.closePath();
          isCurrentlyDrawing = false;
          setIsDrawing(false);
          saveCanvasState();
        }
      };

      // Mouse events
      canvas.addEventListener('mousedown', startDrawing);
      canvas.addEventListener('mousemove', draw);
      canvas.addEventListener('mouseup', endDrawing);
      canvas.addEventListener('mouseout', endDrawing);

      // Touch events
      canvas.addEventListener('touchstart', startDrawing, { passive: false });
      canvas.addEventListener('touchmove', draw, { passive: false });
      canvas.addEventListener('touchend', endDrawing);

      return () => {
        canvas.removeEventListener('mousedown', startDrawing);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', endDrawing);
        canvas.removeEventListener('mouseout', endDrawing);
        canvas.removeEventListener('touchstart', startDrawing);
        canvas.removeEventListener('touchmove', draw);
        canvas.removeEventListener('touchend', endDrawing);
      };
    }, [penColor, penSize]);

    const getCurrentTimestamp = () => {
      const now = new Date();
      return `${now.toISOString().slice(0, 10).replace(/-/g, '')}${now.toTimeString().slice(0, 8).replace(/:/g, '')}`;
    };

    const handleUploadImage = async () => {
      try {
        setLoading(true);
        setError('');
        const canvas = canvasRef.current;
        if (!canvas) throw new Error("Canvas not found");

        const dataUrl = canvas.toDataURL('image/png');
        const blob = await fetch(dataUrl).then(res => res.blob());
        const formData = new FormData();
        formData.append("file", blob, `notes_${userCode}_${getCurrentTimestamp()}.png`);

        const response = await axios.post("/api/uploads/upload-usernotes", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.status === 200) {
          alert("Image uploaded successfully!");
        }
      } catch (err) {
        setError(err.message || "Upload failed");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const handleDownloadImage = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const link = document.createElement('a');
      link.download = `notes_${userCode}_${getCurrentTimestamp()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    const handleDownloadPDF = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`notes_${userCode}_${getCurrentTimestamp()}.pdf`);
    };

    return (
      <div className="flex flex-col items-center w-full min-h-screen bg-gray-100 p-4">
        <div className="flex flex-wrap justify-evenly mb-4">
          <p className="text-blue-700 text-sm text-center">
            Handwritten Notes — Use Mouse / Stylus / Finger
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-4 mb-6 w-full bg-gray-300 p-4 border border-gray-400 rounded-lg shadow-lg justify-center">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 6, 8].map((size) => (
              <button
                key={size}
                onClick={() => setPenSize(size)}
                className={`bg-gray-200 rounded hover:bg-gray-300 p-2 ${penSize === size ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className="bg-black" style={{ width: '20px', height: `${size}px` }}></div>
              </button>
            ))}
          </div>

          <div className="flex items-center flex-wrap gap-2">
            {['black', 'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'violet', 'white'].map((color) => (
              <button
                key={color}
                onClick={() => setPenColor(color)}
                className="w-7 h-7 rounded-full border-2 border-gray-400 hover:border-black"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleFillCanvas} title="Fill Canvas" className="bg-white p-2 rounded hover:bg-gray-100">
              <FontAwesomeIcon icon={faFillDrip} className="text-xl text-blue-600" />
            </button>
            <button onClick={handleUndo} title="Undo" className="bg-white p-2 rounded hover:bg-gray-100">
              <FontAwesomeIcon icon={faUndo} className="text-xl text-yellow-600" />
            </button>
            <button onClick={handleRedo} title="Redo" className="bg-white p-2 rounded hover:bg-gray-100">
              <FontAwesomeIcon icon={faRedo} className="text-xl text-yellow-600" />
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="relative w-full bg-white border border-gray-400 rounded-lg shadow-lg overflow-hidden" style={{ height: '70vh' }}>
          <canvas
            ref={canvasRef}
            className="w-full h-full cursor-crosshair"
            style={{ touchAction: 'none' }}
          />
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap gap-8 justify-center bg-gray-300 p-6 border border-gray-400 rounded-lg shadow-lg w-full">
          <div className="flex flex-col items-center cursor-pointer" onClick={handleDownloadImage}>
            <FontAwesomeIcon icon={faDownload} className="text-3xl hover:text-blue-600" title="Download Image" />
            <span className="text-xs mt-1">Image</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer" onClick={handleDownloadPDF}>
            <FontAwesomeIcon icon={faFilePdf} className="text-3xl hover:text-red-600" title="Download PDF" />
            <span className="text-xs mt-1">PDF</span>
          </div>
          <div className="flex flex-col items-center cursor-pointer" onClick={handleUploadImage}>
            <FontAwesomeIcon icon={faUpload} className="text-3xl hover:text-green-600" title="Upload / Save Note" />
            <span className="text-xs mt-1">Save</span>
          </div>
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faFileAlt} className="text-3xl text-gray-400 cursor-not-allowed" title="OCR Disabled" />
            <span className="text-xs mt-1 text-gray-400">To Text</span>
          </div>
        </div>

        {loading && <p className="mt-4 text-blue-600">Uploading...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}
      </div>
    );
  };

  const SpeechInputComponent = ({ label, name, value, onChange, placeholder, maxLength }) => {
    const handleMicrophoneClick = () => {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
  
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
  
        recognition.onstart = () => {
          // console.log("Speech recognition started");
        };
  
        recognition.onresult = (event) => {
          const speechResult = event.results[0][0].transcript;
          // console.log("Speech result:", speechResult); 
          onChange({
            target: {
              name,
              value: value + ' ' + speechResult,
            },
          });
        };
  
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          alert(`Speech recognition error: ${event.error}`);
      };
  
        recognition.onend = () => {
          // console.log("Speech recognition ended");
        };
  
        recognition.start();
      } else {
        alert('Speech recognition is not supported in this browser. Please use Google Chrome.');
      }
    };
  
    return (
      <div className="text-sm px-4 py-2">
        {/* <label htmlFor={name}>{label}</label> */}
        <button
          className="bg-green-500 text-white w-auto rounded-lg flex items-center hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 transition duration-150 ease-in-out whitespace-nowrap"
          onClick={handleMicrophoneClick}
          aria-label="Start speech recognition"
          >
          <FontAwesomeIcon icon={faMicrophone} className="mr-2" style={{ color: 'white' }} />
          <span>Speak {label}</span>
        </button>
        <textarea
          type={name}
          name={name}
          id={name}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          maxLength={maxLength}
          style={{
            border: '1px solid #ccc',
            padding: '2px',
            backgroundColor: '#f0f0f0',
            color: '#000000',
            maxWidth: '100%',
            maxHeight: '150px',
          }}
        />
      </div>
    );
  };

  return (
    <div className="my-2 mx-auto py-4 bg-white text-black fixed-height-container" style={{ height: "100vh", overflow: "auto" }}>
      <div className="w-full h-full overflow-y-auto bg-blue-700 text-white p-4 rounded-md">
        <div className="mb-4">
          {/* Type Selector */}
          <div className="mb-4">
            <label className="text-sm text-gray-100 ml-1">Type*:</label>
            <select
              value={notesType}
              onChange={(e) => setNotesType(e.target.value)}
              className="w-full border rounded-md p-2 h-10 text-black"
              style={{ width: "300px" }}
            >
              <option value="Chapter Summary">Estimate Bill</option>
              <option value="Keywords">GST Invoice</option>
              <option value="Notes for Exam">Receipt</option>
            </select>
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="text-sm text-gray-100 ml-1">Title*:</label>
            <input
              type="text"
              value={userCommentTitle}
              onChange={(e) => setUserCommentTitle(e.target.value)}
              placeholder="Input title"
              className="w-full border rounded-md p-2 bg-gray-50 text-black"
              style={{ width: "90%" }}
            />
          </div>

          {/* Voice Input */}
          <div className="mb-4">
            <label className="text-sm text-gray-100 ml-1">
              <input
                type="checkbox"
                checked={isAudioInput}
                onChange={isAudioInputChange}
                className="mr-2"
              />
              Voice Input
            </label>
          </div>

          {isAudioInput && (
            <>
              <label className="text-sm text-gray-100 ml-1 block">
                <input
                  type="checkbox"
                  checked={showMicrophoneAlert}
                  onChange={handleShowMicrophoneAlertCheckboxChange}
                  className="mr-2"
                />
                Enable Microphone 🎤
              </label>
              {showMicrophoneAlert && (
                <label className="text-sm text-gray-100 ml-1 block mt-2">
                  <input
                    type="checkbox"
                    checked={isConversationalNotes}
                    onChange={handleConversationalNotesCheckboxChange}
                    className="mr-2"
                  />
                  Make it a Conversational Note
                </label>
              )}
            </>
          )}

          {/* Details */}
          <div className="mb-4">
            <label className="text-sm text-gray-100 ml-1">Details*:</label>
            {showMicrophoneAlert ? (
              <SpeechInputComponent
                label="Memo"
                name="userCommentDetails"
                value={userCommentDetails}
                onChange={(e) => setUserCommentDetails(e.target.value)}
                placeholder="Click Mic and Speak..."
                maxLength="2000"
                className="w-full border rounded-md p-2 bg-gray-50"
              />
            ) : (
              <textarea
                value={userCommentDetails}
                onChange={(e) => setUserCommentDetails(e.target.value)}
                placeholder="Input here..."
                maxLength="2000"
                className="w-full border rounded-md p-2 bg-gray-50 text-black"
                style={{ height: "200px" }}
              />
            )}
          </div>

          {/* Handwriting Section */}
          <div className="mb-4 flex flex-col">
            <button
              className="mr-4 mb-2 w-auto bg-blue-500 text-white px-4 py-2 rounded-md"
              onClick={handleToggleHandwriting}
            >
              {isHandwritingVisible ? "Hide Handwritten Note" : "Show Handwritten Note"}
            </button>

            {isHandwritingVisible && <HandwrittenNote onClose={handleToggleHandwriting} />}
          </div>

          {/* AI Support */}
          <div className="mb-4">
            <label className="text-sm text-gray-100 ml-1">
              <input
                type="checkbox"
                checked={isCheckedAISupport}
                onChange={() => setIsCheckedAISupport(!isCheckedAISupport)}
                className="mr-2"
              />
              I want Gen AI Support for generating my prompt
            </label>
            {isCheckedAISupport && userCommentDetails && (
              <div className="mt-2">
                <AILightComponent
                  param1={notesType}
                  param2={`Memo / Estimate Title: ${userCommentTitle}, Details: ${userCommentDetails}`}
                />
              </div>
            )}
          </div>

          {/* Remarks & Keywords */}
          <div>
            <p className="text-sm mt-2 ml-4 text-gray-100">Remarks:</p>
            <input
              className="text-black ml-4 bg-slate-100 mb-2 w-full p-2"
              style={{ width: "90%" }}
              value={inputRemarks}
              onChange={(e) => setInputRemarks(e.target.value)}
            />
            <p className="text-sm ml-4 text-gray-100">Keywords:</p>
            <input
              className="text-black ml-4 bg-slate-100 mb-2 w-full p-2"
              style={{ width: "90%" }}
              value={sortTags}
              onChange={(e) => setSortTags(e.target.value)}
            />
          </div>

          {errors && !postedComment && (
            <p className="text-red-600 mt-2">{errors}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceBillCreator;